import { produce } from 'immer';
import { FormulaDefinition } from '@/core';
import { QueryData } from '../model';
import {
  ParseFormulaType,
  processFormulasForDataframes,
  processFormulasForSingleValue,
  processFormulasForSingleValueTimeSeries,
  processFormulasForTimeSeries,
} from './formulas';

// appendFormulaResults should only be used for appending to the UseQueryDataResult.queryResults and not modify other fields.
export function appendFormulaResults({
  result,
  formulas,
  parseFormula,
  isLoading,
  isFetching,
  isFetchingMore,
}: {
  result: QueryData[];
  formulas: FormulaDefinition[];
  parseFormula: ParseFormulaType | undefined;
  isFetching: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
}) {
  // If there are no formulas, return the result as is and don't modify it.
  if (!parseFormula || formulas.length === 0) {
    return result;
  }

  return produce(result, (draft) => {
    const enabledFormulas = formulas.filter((f) => !f.spec?.disabled);

    const timeSeries = draft.filter((r): r is QueryData<'TimeSeries'> => r.type === 'TimeSeries');

    const tables = draft.filter((r): r is QueryData<'Table'> => r.type === 'Table');

    const singleValue = draft.filter((r): r is QueryData<'SingleValue'> => r.type === 'SingleValue');

    const singleValueTimeSeries = draft.filter(
      (r): r is QueryData<'SingleValueTimeSeries'> => r.type === 'SingleValueTimeSeries'
    );

    if (timeSeries && timeSeries.length > 0) {
      draft = [
        ...draft,
        ...processFormulasForTimeSeries(
          parseFormula,
          enabledFormulas,
          timeSeries,
          isLoading,
          isFetching,
          isFetchingMore
        ),
      ];
    }
    if (tables && tables.length > 0) {
      draft = [...draft, ...processFormulasForDataframes(parseFormula, enabledFormulas, tables)];
    }
    if (singleValue && singleValue.length > 0) {
      draft = [
        ...draft,
        ...processFormulasForSingleValue(
          parseFormula,
          enabledFormulas,
          singleValue,
          isLoading,
          isFetching,
          isFetchingMore
        ),
      ];
    }
    if (singleValueTimeSeries && singleValueTimeSeries.length) {
      draft = [
        ...draft,
        ...processFormulasForSingleValueTimeSeries(
          parseFormula,
          enabledFormulas,
          singleValueTimeSeries,
          isLoading,
          isFetching,
          isFetchingMore
        ),
      ];
    }
    return draft;
  });
}
